import { delay } from '@/utils'
import { defineStore } from 'pinia'
import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useCameraFrameStore } from './useCameraFrameStore'

type ExpandingProgress = {
  target: 'stacking' | 'stackings' | 'none'
  y: number
  progress: number
  transiting: boolean
  transitingByCSS: boolean
}
type FilterVisibility =
  | 'collapsed'
  | 'to-expand'
  | 'expanding'
  | 'expanded'
  | 'collapsing'

export type ProductSelectVisibility =
  | 'loading'
  | 'snapshotting'
  | 'idle'
  | 'products'
  | 'materials'
export type StackingSelectVisibility =
  | 'hidden'
  | 'loading'
  | 'snapshotting'
  | 'idle'
  | 'bars'
  | 'all-panel-shown'

export const useLayoutStore = defineStore('layout', () => {
  const cameraFrameStore = useCameraFrameStore()

  // 状态
  const isTryOnCameraRendered = ref(false)
  const isDefaultCameraRendered = ref(false)
  const isFromNewWorksToTryOn = ref(false)
  const hasUnreadNetworkError = ref(false)
  const hasManualLoading = ref(false)

  const materialFilterVisibility = ref<FilterVisibility>('collapsed')
  const snapshotButtonVisibility = ref<
    'available' | 'dark' | 'hidden' | 'idle'
  >('idle')
  const snapshotButtonVisibilityChangeTimer = ref<number>()
  const isSnapshotLayerVisible = ref<boolean>(false)
  const isShownByClosingSnapLayer = ref<boolean>(false)

  const drawerState = reactive<ExpandingProgress>({
    target: 'none',
    y: 0,
    progress: 0,
    transiting: false,
    transitingByCSS: false
  })
  const drawerStateClassName = computed(() =>
    drawerState.transiting
      ? drawerState.transitingByCSS
        ? 'drawer-transit-by-css'
        : 'drawer-transit-by-script'
      : 'drawer-not-transit'
  )
  const isDrawerExpaned20Percent = computed(() => drawerState.progress > 0.2)

  const isFilterPanelCollapsed = computed(
    () => materialFilterVisibility.value === 'collapsed'
  )

  const isAllWorksMode = computed(
    () =>
      cameraFrameStore.cameraMode !== 'none' &&
      cameraFrameStore.cameraMode !== 'idle'
  )
  const isAllWorksReady = computed(() => {
    if (isFromNewWorksToTryOn.value) {
      return isAllWorksMode.value && cameraFrameStore.isTryOnCameraMount
    }
    return isAllWorksMode.value
  })

  const gotoAllWorks = async (
    toTryOn: boolean,
    fromNewWorksToTryOn = false
  ) => {
    // console.log('[[ Layout ] Before GoToAllWorks ]', {
    //   tryOn: toTryOn,
    //   fromNew: fromNewWorksToTryOn,
    //   defaultCameraRendered: isDefaultCameraRendered.value
    // })
    let cameraMode: typeof cameraFrameStore.cameraMode
    if (toTryOn) {
      isFromNewWorksToTryOn.value = fromNewWorksToTryOn
      if (!isTryOnCameraRendered.value) {
        isTryOnCameraRendered.value = true
        await nextTick()
      }
      cameraMode = 'try-on'
    } else {
      await cameraFrameStore.stopCamera()
      isFromNewWorksToTryOn.value = false
      if (!isDefaultCameraRendered.value) {
        isDefaultCameraRendered.value = true
        await nextTick()
      }
      cameraMode = 'default'
      if (isTryOnCameraRendered.value && cameraFrameStore.tryOnUrl) {
        cameraMode = 'both'
      }
      if (['none', 'try-on'].includes(cameraFrameStore.cameraMode)) {
        await showManualLoading()
      }
    }
    console.log('[[ Layout ] GoToAllWorks ]', {
      cameraMode,
      defaultCameraRendered: isDefaultCameraRendered.value
    })
    if (toTryOn) {
      await cameraFrameStore.startCamera()
    }
    cameraFrameStore.cameraMode = cameraMode
  }

  const gotoNewWorks = async () => {
    console.log('[[ Layout ] GoToNewWorks ]', {
      defaultCameraShown: cameraFrameStore.isDefaultCameraShown
    })
    cameraFrameStore.pauseCamera()
    if (cameraFrameStore.isDefaultCameraShown) {
      cameraFrameStore.cameraMode = 'idle'
    } else {
      cameraFrameStore.cameraMode = 'none'
    }
  }

  const updatePdpExpandingProgress = (progress: ExpandingProgress) => {
    Object.assign(drawerState, progress)
  }

  const resetPdpExpandingProgress = () => {
    Object.assign(drawerState, {
      target: 'none',
      y: 0,
      progress: 0,
      transiting: false,
      transitingByCSS: false
    })
  }

  const updateSnapshotButtonVisibility = (toHide: boolean, smooth: boolean) => {
    clearTimeout(snapshotButtonVisibilityChangeTimer.value)
    if (smooth) {
      if (toHide) {
        snapshotButtonVisibility.value = 'dark'
        snapshotButtonVisibilityChangeTimer.value = setTimeout(() => {
          snapshotButtonVisibility.value = 'hidden'
        }, 200)
      } else {
        snapshotButtonVisibility.value = 'idle'
        snapshotButtonVisibilityChangeTimer.value = setTimeout(() => {
          snapshotButtonVisibility.value = 'available'
        }, 200)
      }
    } else {
      snapshotButtonVisibility.value = toHide ? 'idle' : 'available'
    }
  }

  const updateSnapshotLayerVisibility = (shown: boolean) => {
    if (shown === isSnapshotLayerVisible.value) return
    isSnapshotLayerVisible.value = shown
    updateSnapshotButtonVisibility(shown, true)
  }

  const showManualLoading = async () => {
    hasManualLoading.value = true
    // 将原本 1000ms 的等待时长拆分为 280ms 的全局等待和 720ms 内部等待
    await delay(280)
    delay(720).then(() => (hasManualLoading.value = false))
  }

  watch(isSnapshotLayerVisible, async visible => {
    if (!visible) {
      isShownByClosingSnapLayer.value = true
      await delay(1024)
    }
    isShownByClosingSnapLayer.value = false
  })

  return {
    drawerState,
    drawerStateClassName,
    isDrawerExpaned20Percent,
    isTryOnCameraRendered,
    isDefaultCameraRendered,
    isAllWorksMode,
    isAllWorksReady,
    hasUnreadNetworkError,
    hasManualLoading,
    materialFilterVisibility,
    snapshotButtonVisibility,
    isFilterPanelCollapsed,
    isSnapshotLayerVisible,
    isShownByClosingSnapLayer,
    isFromNewWorksToTryOn,

    gotoAllWorks,
    gotoNewWorks,
    showManualLoading,
    updatePdpExpandingProgress,
    resetPdpExpandingProgress,
    updateSnapshotButtonVisibility,
    updateSnapshotLayerVisibility
  }
})
